import React, { useCallback, useRef, useState } from "react";
import { FormFieldDto } from "../../Models/FormFieldDto";
import { FieldOption } from "../../Models/FormFieldOption";
import {
  parseBoothRegion,
  serializeBoothRegion,
  BoothRegion,
} from "../../Models/BoothRegion";
import FloorplanUploader from "./FloorplanUploader";
import CommonApi from "../../Api/CommonApi";
import { DetectedBoothResponse } from "../../Models/DetectedBooth";

interface BoothMapEditorProps {
  field: FormFieldDto;
  onChange: (changes: Partial<FormFieldDto>) => void;
}

const BoothMapEditor: React.FC<BoothMapEditorProps> = ({ field, onChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(
    null
  );
  const [drawCurrent, setDrawCurrent] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [newBoothName, setNewBoothName] = useState("");
  const [importJson, setImportJson] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [detectMessage, setDetectMessage] = useState<string | null>(null);
  const pdfDetectInputRef = useRef<HTMLInputElement>(null);

  const options = field.options || [];

  const boothsToOptions = (booths: DetectedBoothResponse[]): FieldOption[] =>
    booths
      .filter(
        (b) =>
          b?.name &&
          b.region?.coords &&
          Array.isArray(b.region.coords) &&
          b.region.coords.length === 4
      )
      .map((b) => ({
        name: String(b.name).trim(),
        value: "",
        amount: 0,
        description: serializeBoothRegion({
          shape: "rect",
          coords: b.region.coords,
        }),
      }));

  const mergeBoothOptions = (
    existing: FieldOption[],
    detected: FieldOption[]
  ): FieldOption[] => {
    const byName = new Map(
      existing.map((o) => [o.name.toLowerCase(), o])
    );
    detected.forEach((m) => byName.set(m.name.toLowerCase(), m));
    return Array.from(byName.values());
  };

  const applyDetectedBooths = (
    booths: DetectedBoothResponse[],
    replace: boolean,
    floorplanUrl?: string
  ) => {
    const mapped = boothsToOptions(booths);
    if (mapped.length === 0) {
      setDetectMessage("No booths detected. Try the PDF or adjust the layout.");
      if (floorplanUrl) onChange({ defaultValue: floorplanUrl });
      return;
    }
    const nextOptions =
      replace || options.length === 0
        ? mapped
        : mergeBoothOptions(options, mapped);

    const changes: Partial<FormFieldDto> = { options: nextOptions };
    if (floorplanUrl) changes.defaultValue = floorplanUrl;
    onChange(changes);

    setDetectMessage(
      `Applied ${mapped.length} booth regions. Review and adjust as needed.`
    );
  };

  const promptApplyBooths = (
    booths: DetectedBoothResponse[],
    floorplanUrl?: string
  ) => {
    if (booths.length === 0) {
      setDetectMessage("No booths detected. Try the PDF or adjust the layout.");
      if (floorplanUrl) onChange({ defaultValue: floorplanUrl });
      return;
    }
    if (options.length === 0) {
      applyDetectedBooths(booths, true, floorplanUrl);
      return;
    }
    const replace = window.confirm(
      `Detected ${booths.length} booths. Replace all ${options.length} existing booths? Click Cancel to merge by booth number.`
    );
    applyDetectedBooths(booths, replace, floorplanUrl);
  };

  const handleFloorplanUploaded = (
    url: string,
    suggestedBooths?: DetectedBoothResponse[]
  ) => {
    if (!url) {
      setDetectMessage("Upload finished but no image URL was returned.");
      return;
    }
    if (suggestedBooths?.length) {
      promptApplyBooths(suggestedBooths, url);
    } else {
      onChange({ defaultValue: url });
      setDetectMessage(null);
    }
  };

  const runPdfDetect = async (file: File) => {
    const form = new FormData();
    form.append("file", file);
    setDetecting(true);
    setDetectMessage(null);
    try {
      const res = await CommonApi.detectBoothsFromPdf(form);
      promptApplyBooths(res.data);
    } catch (err: any) {
      setDetectMessage(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Detection failed."
      );
    } finally {
      setDetecting(false);
    }
  };

  const runAiDetect = async () => {
    if (!field.defaultValue) {
      setDetectMessage("Upload a floorplan image first.");
      return;
    }
    setDetecting(true);
    setDetectMessage(null);
    try {
      const res = await CommonApi.detectBoothsFromImageUrl(field.defaultValue);
      promptApplyBooths(res.data);
    } catch (err: any) {
      setDetectMessage(
        err?.response?.data?.message ||
          err?.response?.data ||
          "AI detection failed. Use auto-detect from PDF (no API key required)."
      );
    } finally {
      setDetecting(false);
    }
  };

  const getPercentPoint = useCallback(
    (clientX: number, clientY: number) => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      return {
        x: ((clientX - rect.left) / rect.width) * 100,
        y: ((clientY - rect.top) / rect.height) * 100,
      };
    },
    []
  );

  const updateOptions = (next: FieldOption[]) => {
    onChange({ options: next });
  };

  const setRegionForIndex = (index: number, region: BoothRegion) => {
    const next = [...options];
    next[index] = {
      ...next[index],
      description: serializeBoothRegion(region),
    };
    updateOptions(next);
  };

  const addBooth = () => {
    const name = newBoothName.trim();
    if (!name) return;
    if (options.some((o) => o.name.toLowerCase() === name.toLowerCase())) {
      return;
    }
    const next = [
      ...options,
      { name, value: "", amount: 0, description: "" },
    ];
    updateOptions(next);
    setSelectedIndex(next.length - 1);
    setNewBoothName("");
  };

  const removeBooth = (index: number) => {
    const next = options.filter((_, i) => i !== index);
    updateOptions(next);
    setSelectedIndex(null);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (selectedIndex == null) return;
    const pt = getPercentPoint(e.clientX, e.clientY);
    if (!pt) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrawing(true);
    setDrawStart(pt);
    setDrawCurrent(pt);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drawing) return;
    const pt = getPercentPoint(e.clientX, e.clientY);
    if (pt) setDrawCurrent(pt);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!drawing || drawStart == null || drawCurrent == null || selectedIndex == null) {
      setDrawing(false);
      setDrawStart(null);
      setDrawCurrent(null);
      return;
    }
    const left = Math.min(drawStart.x, drawCurrent.x);
    const top = Math.min(drawStart.y, drawCurrent.y);
    const width = Math.abs(drawCurrent.x - drawStart.x);
    const height = Math.abs(drawCurrent.y - drawStart.y);
    if (width > 0.5 && height > 0.5) {
      setRegionForIndex(selectedIndex, {
        shape: "rect",
        coords: [
          Math.round(left * 100) / 100,
          Math.round(top * 100) / 100,
          Math.round(width * 100) / 100,
          Math.round(height * 100) / 100,
        ],
      });
    }
    setDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const previewRect =
    drawing && drawStart && drawCurrent
      ? {
          left: Math.min(drawStart.x, drawCurrent.x),
          top: Math.min(drawStart.y, drawCurrent.y),
          width: Math.abs(drawCurrent.x - drawStart.x),
          height: Math.abs(drawCurrent.y - drawStart.y),
        }
      : null;

  const handleImport = () => {
    setImportError(null);
    try {
      const parsed = JSON.parse(importJson) as {
        booths?: { name: string; region?: BoothRegion }[];
      };
      if (!Array.isArray(parsed.booths)) {
        setImportError('JSON must be { "booths": [{ "name": "1", "region": {...} }] }');
        return;
      }
      const imported: FieldOption[] = parsed.booths.map((b) => ({
        name: String(b.name),
        value: "",
        amount: 0,
        description: b.region ? serializeBoothRegion(b.region) : "",
      }));
      updateOptions(imported);
      setImportJson("");
    } catch {
      setImportError("Invalid JSON.");
    }
  };

  return (
    <div className="space-y-4 border rounded p-4 bg-gray-50">
      <h4 className="font-semibold text-gray-800">Booth floorplan</h4>

      <FloorplanUploader onUploaded={handleFloorplanUploaded} />

      <div className="flex flex-wrap gap-2 items-center">
        <input
          ref={pdfDetectInputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void runPdfDetect(file);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="px-3 py-2 bg-emerald-600 text-white rounded text-sm disabled:opacity-50"
          disabled={detecting}
          onClick={() => pdfDetectInputRef.current?.click()}
        >
          {detecting ? "Detecting…" : "Auto-detect from PDF"}
        </button>
        <button
          type="button"
          className="px-3 py-2 bg-violet-600 text-white rounded text-sm disabled:opacity-50"
          disabled={detecting || !field.defaultValue}
          onClick={() => void runAiDetect()}
          title="Requires BoothDetection:OpenAIApiKey in API settings"
        >
          Auto-detect with AI (image)
        </button>
      </div>
      {detectMessage && (
        <p className="text-sm text-gray-700 bg-white border rounded p-2">
          {detectMessage}
        </p>
      )}
      <p className="text-xs text-gray-500">
        For 90+ booths: upload the original PDF (fastest) or use AI on the image if
        an OpenAI key is configured on the API.
      </p>

      {field.defaultValue ? (
        <div
          ref={containerRef}
          className="relative inline-block max-w-full select-none touch-none border rounded overflow-hidden bg-white"
          style={{ cursor: selectedIndex != null ? "crosshair" : "default" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <img
            key={field.defaultValue}
            src={field.defaultValue}
            alt="Floorplan"
            className="block max-w-full h-auto pointer-events-none"
            draggable={false}
            onError={() =>
              setDetectMessage(
                "Floorplan image failed to load. Check the URL or re-upload."
              )
            }
          />
          {options.map((opt, idx) => {
            const region = parseBoothRegion(opt.description);
            if (!region) return null;
            const [left, top, width, height] = region.coords;
            const isSelected = selectedIndex === idx;
            return (
              <div
                key={`${opt.name}-${idx}`}
                className={`absolute border-2 box-border pointer-events-none ${
                  isSelected
                    ? "border-blue-600 bg-blue-500/25"
                    : "border-green-600 bg-green-500/20"
                }`}
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                  width: `${width}%`,
                  height: `${height}%`,
                }}
                title={opt.name}
              />
            );
          })}
          {previewRect && (
            <div
              className="absolute border-2 border-dashed border-blue-500 bg-blue-400/20 pointer-events-none"
              style={{
                left: `${previewRect.left}%`,
                top: `${previewRect.top}%`,
                width: `${previewRect.width}%`,
                height: `${previewRect.height}%`,
              }}
            />
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">
          Upload a floorplan above to preview the map.
        </p>
      )}

      <p className="text-xs text-gray-600">
        Select a booth below, then click and drag on the image to draw its
        region.
      </p>

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[120px]">
          <label className="text-xs font-medium">New booth number</label>
          <input
            className="w-full p-2 border rounded text-sm"
            value={newBoothName}
            onChange={(e) => setNewBoothName(e.target.value)}
            placeholder="e.g. 12"
            onKeyDown={(e) => e.key === "Enter" && addBooth()}
          />
        </div>
        <button
          type="button"
          className="px-3 py-2 bg-blue-600 text-white rounded text-sm"
          onClick={addBooth}
        >
          Add booth
        </button>
      </div>

      <ul className="space-y-1 max-h-48 overflow-y-auto">
        {options.map((opt, idx) => {
          const hasRegion = !!parseBoothRegion(opt.description);
          return (
            <li
              key={`booth-${idx}-${opt.name}`}
              className={`flex items-center justify-between gap-2 p-2 rounded text-sm ${
                selectedIndex === idx ? "bg-blue-100" : "bg-white border"
              }`}
            >
              <button
                type="button"
                className="text-left flex-1 font-medium"
                onClick={() => setSelectedIndex(idx)}
              >
                Booth {opt.name}
                {!hasRegion && (
                  <span className="text-amber-600 font-normal ml-2">
                    (no region)
                  </span>
                )}
              </button>
              <button
                type="button"
                className="text-red-600 text-xs"
                onClick={() => removeBooth(idx)}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex gap-3 text-xs">
        <span className="inline-flex items-center gap-1">
          <span className="w-4 h-4 border-2 border-green-600 bg-green-500/20 inline-block" />
          Defined region
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-4 h-4 border-2 border-blue-600 bg-blue-500/25 inline-block" />
          Selected
        </span>
      </div>

      <details className="text-sm">
        <summary className="cursor-pointer text-gray-700">
          Import booths (JSON)
        </summary>
        <textarea
          className="w-full mt-2 p-2 border rounded font-mono text-xs"
          rows={4}
          value={importJson}
          onChange={(e) => setImportJson(e.target.value)}
          placeholder='{"booths":[{"name":"1","region":{"shape":"rect","coords":[10,20,5,5]}}]}'
        />
        {importError && (
          <p className="text-red-600 text-xs mt-1">{importError}</p>
        )}
        <button
          type="button"
          className="mt-2 px-3 py-1 border rounded text-sm"
          onClick={handleImport}
        >
          Import
        </button>
      </details>
    </div>
  );
};

export default BoothMapEditor;
